import { authService } from "../utils/authService";
import { errorHandler } from "../components/shared/errorhandler";
import { dedupeSession, clearSessionCache } from "./sessionCache";
import type { SessionResponse, ResolveInstanceResponse } from "../../shared/types/rbac";

/**
 * A refused or failed auth call, with the server's `code` when it sent one
 * (ACCOUNT_BLOCKED, NO_ASSIGNMENT, ...), so the no-access screen can say why.
 */
export class AuthRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "AuthRequestError";
  }
}

async function requestError(response: Response): Promise<AuthRequestError> {
  const body = await response.json().catch(() => ({}));
  return new AuthRequestError(
    body.error || `Request failed with status ${response.status}`,
    response.status,
    typeof body.code === "string" ? body.code : null,
  );
}

/**
 * Create a session after B2C login.
 * Activates pending users and returns accessible instances.
 * Retries on 503 (server still initializing) up to 5 times.
 * Replaces: login-studio + getdefaultinstance
 */
const fetchSession = async (): Promise<SessionResponse> => {
  const maxRetries = 5;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const response = await fetch("/api/v1/auth/session", {
      method: "POST",
      headers: await authService.getAuthorizationHeader(),
    }).then(errorHandler);

    if (response.status === 503 && attempt < maxRetries - 1) {
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }

    if (!response.ok) {
      throw await requestError(response);
    }

    return response.json();
  }

  throw new Error("Server is still starting up. Please refresh the page in a moment.");
};

/**
 * Create a session after B2C login, deduplicated across concurrent callers.
 * Both the B2C callback and the header's tenant switcher need this payload on
 * load; without a shared cache each fires its own request.
 *
 * Cache state lives in ./sessionCache so authService can clear it on logout
 * without creating a circular import.
 */
export const createSession = (): Promise<SessionResponse> => dedupeSession(fetchSession);

export { clearSessionCache };

/**
 * Resolve access for a specific instance.
 * Returns role, permissions, and flags in a single call.
 * Replaces: validateinstance-studio + access
 */
export const resolveInstance = async (instanceId: string): Promise<ResolveInstanceResponse> => {
  return fetch("/api/v1/auth/resolve-instance", {
    method: "POST",
    headers: await authService.getAuthorizationHeader(),
    body: JSON.stringify({ instanceId }),
  })
    .then(errorHandler)
    .then(async (response) => {
      // A refusal (a blocked account's 403) is not an answer about the instance.
      if (!response.ok) throw await requestError(response);
      return response.json();
    });
};
